package ui.campaign.detailv2.editor

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.Crossfade
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.StringResource
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tools.rememberString
import ui.campaign.detailv2.editor.page.*
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperE
import ui.widget.*
import kotlin.time.Duration.Companion.seconds

data class CampaignEditorArgs(
    var companyId: Long = -1,
    var campaignId: Long = -1,
    var backStack: Int = 1
)

@Composable
@ComposableNavNode(CampaignEditorArgs::class)
fun CampaignEditorUI() {
    with(CampaignEditorScope.rememberScope()) {
        Box(Modifier.fillMaxSize()) {
            Crossfade(
                targetState = vm.state,
                modifier = Modifier.fillMaxSize()
            ) {
                when (val state = it) {
                    is DataState.Error<*> -> ErrUI()
                    is DataState.Idle<*>,
                    is DataState.Loading<*> -> LoadingUI()

                    is DataState.Result<CampaignWrapperE> -> ContentUI(state.nnvl)
                }
            }
        }
        AnimatedVisibility(
            visible = vm.showLoader,
            enter = fadeIn(),
            exit = fadeOut(),
            modifier = Modifier.fillMaxSize()
        ) {
            Spacer(
                Modifier.fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.5F))
                    .blockTouch()
            )
        }
    }
}


@Composable
private fun CampaignEditorScope.ContentUI(nnvl: CampaignWrapperE) {
    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix = Res.string.layer_top_detail_editor__title.rememberString(),
            titleSuffix =
                if (nnvl.isNewCampaign) Res.string.layer_top_detail_editor__subtitle_new.rememberString()
                else nnvl.originName,
            onUp = { nav.up() })

        InlineTabRow(
            selectedTabIndex = vm.tabIndex,
            tabsCount = vm.tabs.size,
            itemMaxWidth = 192.dp
        ) { index ->
            InlineTab(
                index = index,
                selectedTabIndex = vm.tabIndex,
                label = vm.tabs[index].name
            ) {
                action(CampaignEditorVM.Action.PageExact(index))
            }
        }

        Crossfade(
            targetState = vm.tabIndex,
            modifier = Modifier.fillMaxSize()
        ) { index ->
            when (vm.tabs[index]) {
                CampaignEditorVM.Tab.General00 -> CampaignPage00General(nnvl)
                CampaignEditorVM.Tab.Location01 -> CampaignPage01Location(nnvl)
                CampaignEditorVM.Tab.Visual02 -> CampaignPage02Visual(nnvl)
                CampaignEditorVM.Tab.Offset03 -> CampaignPage03Offsets(nnvl)
                CampaignEditorVM.Tab.DynamicVisual04 -> CampaignPage04VisualDynamic(nnvl)
                CampaignEditorVM.Tab.Effect05 -> CampaignPage05Effect()
                CampaignEditorVM.Tab.Application06 -> CampaignPage06Application(nnvl)
                CampaignEditorVM.Tab.MiniApp07 -> CampaignPage07MiniApp(nnvl)
                CampaignEditorVM.Tab.Summary08 -> CampaignPage08Summary(nnvl)
            }
        }
    }
}

@Composable
fun CampaignEditorScope.NextButton(
    label: StringResource = Res.string.layer_top_detail_editor__act_next,
    skipToSummary: Boolean = false
) {
    var lock by remember {
        mutableStateOf(false)
    }
    LaunchedEffect(lock) {
        if (lock) {
            delay(1.seconds)
            lock = false
        }
    }
    val actions: @Composable () -> Unit = {
        Button(
            modifier = Modifier.width(256.dp).padding(8.dp),
            enabled = !lock,
            onClick = {
                lock = true
                action(CampaignEditorVM.Action.PageNext)
            }) {
            Text(
                text = label.rememberString(),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
        }
        if (skipToSummary) {
            Button(
                modifier = Modifier.width(256.dp).padding(8.dp),
                enabled = !lock,
                onClick = {
                    lock = true
                    action(CampaignEditorVM.Action.SkipToSummary)
                }) {
                Text(
                    text = Res.string.layer_top_detail_editor__act_skip_to_summary.rememberString(),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
    Box(Modifier.fillMaxWidth()) {
        if (display.isMobileOrSmall) {
            Column(
                modifier = Modifier.padding(8.dp).align(Alignment.Center)
            ) { actions() }
        } else {
            Row(
                modifier = Modifier.padding(8.dp).align(Alignment.Center),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) { actions() }
        }
    }
}